import { FeatureBox3D } from 'features/assetViz/objects/features/procedural/FeatureBox3D'
import { FeatureCylinder3D } from 'features/assetViz/objects/features/procedural/FeatureCylinder3D'
import { FeatureLabel3D } from 'features/assetViz/objects/features/procedural/FeatureLabel3D'
import { SurfaceFeature3D } from 'features/assetViz/objects/features/SurfaceFeature3D'
import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import React from 'react'
import {
  ChockNominalFittingSize,
  GeckoComponentCoords,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoShipDeckChock,
} from 'types'

/**
 * A component representing a chock on the surface of a ship deck component
 * @param model             The chock model (from the asset model)
 * @constructor
 */
export const DeckChockFeature3D: React.FC<{
  model: GeckoShipDeckChock
}> = ({ model }) => {
  //----------------------------------------------------------------------------

  const { bounds } = useSurfaceProps()
  const getSize = (nominalFittingSize: ChockNominalFittingSize) => {
    switch (nominalFittingSize) {
      case 'US6':
        return { width: 0.13335, height: 0.3302 }
      case 'US10':
        return { width: 0.1651, height: 0.4826 }
      case 'US13':
        return { width: 0.1905, height: 0.5842 }
      case 'US16':
        return { width: 0.2286, height: 0.7112 }
      case 'US20':
        return { width: 0.4064, height: 0.98425 }
      case 'US24':
        return { width: 0.3429, height: 1.016 }
      default:
        return { width: 0.13335, height: 0.3302 }
    }
  }

  const size = getSize(model.nominalFittingSize)

  const width = React.useMemo(() => {
    return GeckoMeasurement.CreateFromValue(
      size.width,
      GeckoMeasurementUnit.Meters
    )
  }, [size.width])

  const height = React.useMemo(() => {
    return GeckoMeasurement.CreateFromValue(
      size.height,
      GeckoMeasurementUnit.Meters
    )
  }, [size.height])

  const radius = React.useMemo(() => {
    return width.scale(0.4)
  }, [width])

  const { labelCoords, positionLabelLeft } = React.useMemo(() => {
    const positionLabelLeft =
      model.geometry.x.num < bounds.start.x.plus(bounds.end.x).scale(0.5).num

    const x = positionLabelLeft
      ? model.geometry.x.minus(width.scale(0.5))
      : model.geometry.x.plus(width.scale(0.5))

    const y = model.geometry.y
    const labelCoords = GeckoComponentCoords.CreateFromMeasurements(
      bounds.componentID,
      x,
      y
    )
    return { labelCoords, positionLabelLeft }
  }, [model, bounds, width])

  //----------------------------------------------------------------------------

  return (
    <>
      <FeatureLabel3D
        feature={model}
        coords={labelCoords}
        anchor={positionLabelLeft ? 'right' : 'left'}
      />
      <SurfaceFeature3D model={model}>
        <FeatureBox3D
          feature={model}
          position={[0, 0, 0]}
          width={width.num}
          height={height.num}
          depth={0.1}
          debugColor={'purple'}
        />
        <FeatureCylinder3D
          feature={model}
          position={[0, 0, -height.num / 2]}
          radius={radius.num}
          height={0.2}
          debugColor={'pink'}
          thetaStart={(3 / 2) * Math.PI}
          thetaEnd={Math.PI / 2}
        />
        <FeatureCylinder3D
          feature={model}
          position={[0, 0, height.num / 2]}
          radius={radius.num}
          height={0.2}
          debugColor={'pink'}
          thetaStart={Math.PI / 2}
          thetaEnd={(3 / 2) * Math.PI}
        />
      </SurfaceFeature3D>
    </>
  )
}
